import { Box, Typography } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useEffect, useMemo } from "react"
import { useFieldArray, useFormContext, useWatch } from "react-hook-form"

import {
  BLANK_ATYPICAL_SURCHARGE_ITEM,
  BLANK_SHIPPING_COST_ITEM,
  COST_BREAKDOWN_CURRENCY_FIELD_PROPS,
  SHIPPING_TYPE_OPTIONS,
} from "../../constants"
import type { CostBreakdownFormValues } from "../../schemas"
import { calculateSharedGrCost } from "../../utils/costCalculations"

import { Button } from "@/components/common/Button"
import { FormNumericField, FormSelect, FormTextField } from "@/components/UiKit/FormControls"
import type { GoodsReceipt } from "@/graphql/codegen/graphql"

interface CostBreakdownFormProps {
  goodsReceipt?: GoodsReceipt
}

export const CostBreakdownForm: FC<CostBreakdownFormProps> = ({ goodsReceipt: _ }) => {
  const { control, trigger, setValue } = useFormContext<CostBreakdownFormValues>()

  const { fields: shippingCostFields, append: appendShippingCost } = useFieldArray({
    control,
    name: "shippingCosts",
  })

  const { fields: atypicalSurchargeFields, append: appendAtypicalSurcharge } = useFieldArray({
    control,
    name: "atypicalSurcharges",
  })

  // Watch all form values to trigger validation when they change
  const watchedShippingCosts = useWatch({ control, name: "shippingCosts" })
  const watchedAtypicalSurcharges = useWatch({ control, name: "atypicalSurcharges" })
  const watchedImportFees = useWatch({ control, name: "importFees" })
  const watchedInsurance = useWatch({ control, name: "insurance" })
  const watchedMpf = useWatch({ control, name: "mpf" })
  const watchedHmf = useWatch({ control, name: "hmf" })

  const sharedGRCost = useMemo(() => {
    return calculateSharedGrCost({
      shippingCosts: watchedShippingCosts,
      atypicalSurcharges: watchedAtypicalSurcharges,
      importFees: watchedImportFees,
      insurance: watchedInsurance,
      mpf: watchedMpf,
      hmf: watchedHmf,
    })
  }, [watchedShippingCosts, watchedAtypicalSurcharges, watchedImportFees, watchedInsurance, watchedMpf, watchedHmf])

  useEffect(() => {
    setValue("sharedGrCost", sharedGRCost)
  }, [sharedGRCost, setValue])

  useEffect(() => {
    trigger("shippingCosts")
  }, [watchedShippingCosts, trigger])

  useEffect(() => {
    trigger("atypicalSurcharges")
  }, [watchedAtypicalSurcharges, trigger])

  const handleAddShippingCost = () => {
    appendShippingCost(BLANK_SHIPPING_COST_ITEM)
  }

  const handleAddAtypicalSurcharge = () => {
    appendAtypicalSurcharge(BLANK_ATYPICAL_SURCHARGE_ITEM)
  }

  return (
    <Box className='space-y-6' data-testid='cost-breakdown-form'>
      <Box>
        <Box className='space-y-6'>
          {shippingCostFields.map((field, index) => (
            <Box key={field.id} className='flex items-start gap-6'>
              <Box className='flex-1'>
                <FormSelect
                  control={control}
                  name={`shippingCosts.${index}.shippingType`}
                  label='Shipping Type Identifier'
                  options={SHIPPING_TYPE_OPTIONS}
                  data-testid={`shipping-type-${index}-select`}
                />
              </Box>
              <Box className='flex-1'>
                <FormNumericField
                  control={control}
                  name={`shippingCosts.${index}.cost`}
                  label='Shipping Cost'
                  data-testid={`shipping-cost-${index}-input`}
                  {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
                />
              </Box>
            </Box>
          ))}
          <Box className='flex justify-end pt-2'>
            <Button
              appearance='text'
              size='small'
              startIcon={<AddOutlined />}
              onClick={handleAddShippingCost}
              data-testid='add-shipping-cost-button'
            >
              Shipping Cost
            </Button>
          </Box>
        </Box>
      </Box>

      <Box className='space-y-6'>
        <Box className='flex items-start gap-6'>
          <FormNumericField
            control={control}
            name='importFees'
            label='Import Fees'
            data-testid='import-fees-input'
            {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
          />

          <FormNumericField
            control={control}
            name='insurance'
            label='Insurance'
            data-testid='insurance-input'
            {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
          />
        </Box>

        <Box className='flex items-start gap-6'>
          <FormNumericField
            control={control}
            name='mpf'
            label='Merchandise Processing Fee'
            data-testid='mpf-input'
            {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
          />

          <FormNumericField
            control={control}
            name='hmf'
            label='Harbor Maintenance Fee'
            data-testid='hmf-input'
            {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
          />
        </Box>
      </Box>

      <Box>
        <Typography variant='subtitle1' className='mb-4 text-gray-900'>
          Atypical Surcharge
        </Typography>

        <Box className='space-y-6'>
          {atypicalSurchargeFields.map((field, index) => (
            <Box key={field.id} className='flex items-start gap-6'>
              <Box className='flex-1'>
                <FormTextField
                  control={control}
                  name={`atypicalSurcharges.${index}.description`}
                  label='Description'
                  slotProps={{
                    htmlInput: { maxLength: 50 },
                  }}
                  data-testid={`atypical-description-${index}-input`}
                />
              </Box>
              <Box className='flex-1'>
                <FormNumericField
                  control={control}
                  name={`atypicalSurcharges.${index}.amount`}
                  label='Amount'
                  data-testid={`atypical-amount-${index}-input`}
                  {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
                />
              </Box>
            </Box>
          ))}

          <Box className='flex justify-end pt-2'>
            <Button
              appearance='text'
              size='small'
              startIcon={<AddOutlined />}
              onClick={handleAddAtypicalSurcharge}
              data-testid='add-atypical-surcharge-button'
            >
              Additional Cost
            </Button>
          </Box>
        </Box>
      </Box>

      <Box>
        <FormNumericField
          control={control}
          name='sharedGrCost'
          label='Shared GR Cost'
          disabled
          data-testid='shared-gr-cost-input'
          {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
          slotProps={{
            inputLabel: {
              className: "font-bold",
            },
          }}
        />
      </Box>
    </Box>
  )
}
